/* ==========================================================================
   Lille Björn: sticky Add to cart bar, Option B, "the bar is the drawer"
   Concept 06, "The Button". Release 1.1.3 (8 Oct 2026).
   assets/lb-sticky-atc-b.js. Load with defer, after bundle.js.
   Pairs with assets/lb-sticky-atc-b.css.

   What it does
   - Restyles the theme's sticky bar as one of the theme's drawers (the CSS):
     sand surface, hairline edges, plinth photo, the theme's Lora button.
   - The bar's own button is the only trigger. While any option is still to
     choose (Fitted's label: "Select size"), a tap opens the drawer in place,
     at the first option still open, instead of scrolling the page up to the
     picker. With everything chosen it reads "Add to cart" and adds, exactly
     as before: that click is never touched.
   - The drawer: every option as a row of chips, plus the colour swatches.
     It opens up from the bottom on phones (the button stays at its foot) and
     down from the bar on desktop, over a warm blurred backdrop.
   - Phones: beside the photo, a small label over the price. The label is the
     collection until something is chosen, then the choices themselves
     ("Collar XS, 3 cm · Leash 2 cm"). The photo and label open the drawer.
   - Desktop: once something is chosen, the choices under the title, as a quiet
     text button that reopens the drawer. The drawer has a footer with the
     price and the same button, next to the chips.
   - Price: the page's own current price for the chosen variant (the theme's
     bar prints the product's lowest price and never updates it), with the
     regular price struck through when the variant is on sale.
   - Guidance: after a pick, the next option still to choose scrolls into view
     and its name lights in brass; once complete, the button is ringed in
     brass for a moment.
   - Phones: the Shopify Inbox chat bubble is lifted above the bar while the
     bar shows, and fades out while the drawer is open.
   - Desktop: while the theme pins its header (after Add to cart, on scroll
     up), the bar sits just below it instead of being covered.

   What it never changes (the bar's logic belongs to the theme and Fitted)
   - The theme's <select class="js-variant-select-item"> elements stay in the
     bar, hidden, as the source of truth. A chip sets select.value and fires a
     bubbling `change`: exactly what bundle.js (pdpStickyAtc) mirrors to the
     page's picker, and what lb-fitted-size.js listens for.
   - "Chosen" is read from Fitted's own picker on the page: an option counts as
     chosen once its radio group there has a checked input. On live, Fitted
     asks for every option in turn (size, then each width).
   - The button element, its label (Fitted writes it), its disabled state and
     its click forwarding. Colour swatches are the theme's links, cloned.
   - When the bar shows (pdpIntersect).

   Safety
   - Every style is gated on .lb-sbb, which this script adds. If the script
     doesn't run, or a bar has no button, the bar is exactly today's.
   - Loaded twice by mistake, it runs once.
   ========================================================================== */
(function () {
  'use strict';
  if (window.LBStickyATCB) return;

  var BARS = '.pdp-sticky-atc-desktop, .pdp-sticky-atc-mobile';
  var PICKER = '.main-pdp-section-redesign [data-lb-fitted]';
  var BUTTON = '.js-product-form-submit-btn';
  var CLOSE = '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" focusable="false"><path d="M1 1l10 10M11 1 1 11" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"/></svg>';
  var uid = 0;
  var states = [];

  /* ---------- helpers ---------- */
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  /* "3cm (1.18 inch)" -> { main: "3 cm", sub: "1.18 in" } */
  function splitUnits(text) {
    var m = String(text).match(/^([\d.,]+)\s*cm\s*\(([\d.,]+)\s*(inch|in|zoll)\)\s*(?:[·\-–]\s*)?(.*)$/i);
    if (!m) return { main: String(text).trim(), sub: '' };
    return { main: m[1] + ' cm', sub: m[2] + ' ' + (/zoll/i.test(m[3]) ? 'Zoll' : 'in') };
  }

  /* ---------- what the shopper has chosen (mirrors Fitted) ----------
     At page load Fitted has nothing checked in any group, while the bar's
     dropdowns hold each option's first value. An option counts as chosen
     once its Fitted group (radios named "...-<index>") has a checked input.
     Options without a Fitted group, and products without Fitted (e.g. a
     leash with no size), count as chosen. */
  function chosenMask(st) {
    var picker = document.querySelector(PICKER);
    return st.opts.map(function (o, i) {
      if (!picker) return true;
      var group = Array.prototype.filter.call(picker.querySelectorAll('input[type="radio"]'), function (r) {
        return new RegExp('-' + i + '$').test(r.name || '');
      });
      if (!group.length) return true;
      return group.some(function (r) { return r.checked; });
    });
  }
  function firstOpen(st) { var i = chosenMask(st).indexOf(false); return i < 0 ? null : i; }

  /* ---------- reading the theme's markup ---------- */
  function readOptions(bar) {
    return Array.prototype.map.call(bar.querySelectorAll('select.js-variant-select-item'), function (sel, i) {
      var w = sel.closest('.js-selectbox-wrapper');
      var nameEl = w && w.querySelector('.form__label .h6');
      var name = nameEl ? nameEl.textContent.trim() : String(sel.name || '').replace(/^options\[|\]$/g, '') || ('Option ' + (i + 1));
      return { sel: sel, name: name };
    });
  }
  function readColours(bar, mobile) {
    var current = mobile
      ? bar.querySelector('.pdp-sticky-atc-mobile__main [\\@click] span, .pdp-sticky-atc-mobile__main .tw-font-Lora')
      : bar.querySelector('.color-name');
    var thumb = mobile ? bar.querySelector('.pdp-sticky-atc-mobile__main img') : null;
    var links = Array.prototype.map.call(bar.querySelectorAll('ul a[href]'), function (a) {
      var img = a.querySelector('img');
      return {
        href: a.getAttribute('href'), target: a.getAttribute('target'),
        img: img ? img.getAttribute('src') : '',
        label: (a.textContent || '').trim() || (img && img.alt) || '',
        current: a.className.indexOf('tw-border-black') > -1 || !!a.querySelector('.tw-border-black')
      };
    });
    return { name: current ? current.textContent.trim() : '', thumb: thumb ? thumb.getAttribute('src') : '', links: links };
  }

  /* ---------- prices, in any currency ----------
     The theme prints "$142.20 USD", "€129,95 EUR", "£110.00 GBP" or
     "Rp 2.593.650,00 IDR". The amount is kept exactly as the theme formats
     it; only the trailing ISO code is split off and set small. */
  function priceEl() {
    var d = document.querySelector('.pdp-sticky-atc-desktop');
    return d && d.querySelector('.tw-flex.tw-items-center > div.tw-whitespace-nowrap');
  }
  /* The current price, as the product page shows it for the chosen variant
     (the theme updates the page's price; its sticky bar prints the product's
     lowest price once and never updates it). On sale, `was` is the regular
     price. Falls back to the bar's own price text. */
  var PAGE_PRICE = '.main-pdp-section-redesign .price';
  function splitCode(text) {
    var m = String(text).trim().match(/^(.*?)\s+([A-Z]{3})$/);
    return m ? { amt: m[1], cur: m[2] } : { amt: String(text).trim(), cur: '' };
  }
  function currentPrice() {
    var box = document.querySelector(PAGE_PRICE);
    var shown = function (n) { return n && !n.classList.contains('d-none') && getComputedStyle(n).display !== 'none'; };
    if (box) {
      var sale = box.querySelector('.price__sale'), reg = box.querySelector('.price__regular'), now = null, was = '';
      if (shown(sale)) {
        now = sale.querySelector('.price-item--last');
        var s = sale.querySelector('s');
        was = s ? splitCode(s.textContent).amt : '';
      } else if (reg) {
        now = reg.querySelector('.price-item--last') || reg.querySelector('.price-item');
      }
      if (now && now.textContent.trim()) {
        var p = splitCode(now.textContent);
        return { amt: p.amt, cur: p.cur, was: was && was !== p.amt ? was : '' };
      }
    }
    var own = priceEl();
    if (!own) return null;
    if (!own.dataset.lbSbbText) own.dataset.lbSbbText = own.textContent.trim();   /* the theme's text, before we split it */
    var q = splitCode(own.dataset.lbSbbText);
    return { amt: q.amt, cur: q.cur, was: '' };
  }
  /* Desktop: the bar's price box shows the current price, the regular price
     struck through on sale, and the ISO code small. Rewritten only when the
     price changes. */
  function renderPrice(p) {
    var box = priceEl();
    if (!box || !p) return;
    var key = p.amt + '|' + p.was + '|' + p.cur;
    if (box.getAttribute('data-lb-sbb-price') === key) return;
    if (!box.dataset.lbSbbText) box.dataset.lbSbbText = box.textContent.trim();
    box.textContent = '';
    box.appendChild(el('span', 'lb-sbb__amt', p.amt));
    if (p.was) { var w = el('s', 'lb-sbb__was', p.was); box.appendChild(w); }
    if (p.cur) box.appendChild(el('span', 'lb-sbb__cur', p.cur));
    box.classList.add('lb-sbb__price');
    box.setAttribute('data-lb-sbb-price', key);
  }
  /* The collection ("Vero Classic": the part after " - " in the title). */
  function collection() {
    var t = document.querySelector('.pdp-sticky-atc-desktop .product-title');
    var parts = (t ? t.textContent.trim() : '').split(/\s+-\s+/);
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }
  /* What has been chosen, in words. Sets name their pieces: "Collar size" +
     "Collar width" + "Leash width" read "Collar XS, 3 cm · Leash 2 cm"; a
     single product reads "Size XL" or "Size M · Width 3 cm". `short` is the
     values alone. Options not chosen yet are left out. */
  var ATTR = /^(size|width|length|colou?r|style|finish)$/i;
  function summary(st, mask) {
    var groups = [], vals = [];
    st.opts.forEach(function (o, i) {
      if (!mask[i]) return;
      var v = splitUnits(o.sel.value).main;
      vals.push(v);
      var w = o.name.trim().split(/\s+/);
      var piece = w.length > 1 && ATTR.test(w[w.length - 1]) ? w.slice(0, -1).join(' ') : '';
      var last = groups[groups.length - 1];
      if (piece && last && last.piece === piece) last.vals.push(v);
      else groups.push({ piece: piece, label: piece || o.name.trim(), vals: [v] });
    });
    return {
      full: groups.map(function (g) { return g.label + ' ' + g.vals.join(', '); }).join(' · '),
      short: vals.join(' · '),
      any: vals.length > 0
    };
  }

  /* ---------- build ---------- */
  function build(bar) {
    if (bar.classList.contains('lb-sbb')) return;
    var mobile = bar.classList.contains('pdp-sticky-atc-mobile');
    var atc = bar.querySelector(BUTTON);
    var opts = readOptions(bar);
    var colours = readColours(bar, mobile);
    if (!atc) return;                                            /* no button: leave the bar alone */
    if (!opts.length && colours.links.length < 2) return;      /* nothing to choose: leave the bar alone */
    var main = mobile ? bar.querySelector('.pdp-sticky-atc-mobile__main') : null;
    var slot = main && main.children[1];
    if (mobile && !slot) return;                                 /* unexpected markup: leave the bar alone */

    var id = 'lb-sbb-' + (++uid);
    var st = { bar: bar, mobile: mobile, atc: atc, opts: [], colours: colours, open: false };

    /* The drawer: part of the bar, on the bar's surface. */
    var panel = el('div', 'lb-sbb__panel');
    panel.id = id + '-panel';
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-labelledby', id + '-title');
    panel.hidden = true;
    var inner = el('div', 'lb-sbb__inner');
    var phead = el('div', 'lb-sbb__phead');
    var ptitle = el('p', 'lb-sbb__ptitle', 'Your selection');
    ptitle.id = id + '-title';
    var close = el('button', 'lb-sbb__close');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = CLOSE;
    phead.appendChild(ptitle); phead.appendChild(close);
    var pbody = el('div', 'lb-sbb__pbody');
    inner.appendChild(phead); inner.appendChild(pbody);
    panel.appendChild(inner);
    /* Desktop: a footer next to the chips, with the price and the same button
       (a proxy: it clicks the bar's own button, so every rule above applies). */
    if (!mobile) {
      var pfoot = el('div', 'lb-sbb__pfoot');
      var fprice = el('span', 'lb-sbb__fprice');
      var fbtn = el('button', 'lb-sbb__fbtn');
      fbtn.type = 'button';
      fbtn.addEventListener('click', function () { atc.click(); });
      pfoot.appendChild(fprice); pfoot.appendChild(fbtn);
      inner.appendChild(pfoot);
      st.foot = { price: fprice, btn: fbtn };
    }

    /* Colour: the theme's own sibling-product links, as round swatches. */
    if (colours.links.length > 1) {
      var cg = el('div', 'lb-sbb__grp lb-sbb__grp--colour');
      var ch = el('div', 'lb-sbb__ghead');
      ch.appendChild(el('span', 'lb-sbb__gname', 'Colour'));
      ch.appendChild(el('span', 'lb-sbb__gval', colours.name));
      var sw = el('div', 'lb-sbb__swatches');
      colours.links.forEach(function (c) {
        var a = el('a', 'lb-sbb__swatch' + (c.current ? ' is-on' : ''));
        a.href = c.href;
        if (c.target) a.target = c.target;
        if (c.target === '_blank') a.rel = 'noopener';
        a.setAttribute('aria-label', c.label + (c.current ? ' (current)' : ''));
        if (c.current) a.setAttribute('aria-current', 'true');
        var im = el('img'); im.src = c.img; im.alt = ''; im.width = 34; im.height = 34; im.loading = 'lazy';
        a.appendChild(im);
        sw.appendChild(a);
      });
      cg.appendChild(ch); cg.appendChild(sw);
      pbody.appendChild(cg);
    }

    /* Options: a legend (name, chosen value) and one chip per value. */
    opts.forEach(function (o, i) {
      var g = el('div', 'lb-sbb__grp');
      var gh = el('div', 'lb-sbb__ghead');
      var gn = el('span', 'lb-sbb__gname', o.name);
      gn.id = id + '-o' + i;
      var gv = el('span', 'lb-sbb__gval');
      gh.appendChild(gn); gh.appendChild(gv);
      var chips = el('div', 'lb-sbb__bar');
      chips.setAttribute('role', 'radiogroup');
      chips.setAttribute('aria-labelledby', gn.id);
      var cells = Array.prototype.map.call(o.sel.options, function (opt) {
        var s = splitUnits(opt.value);
        var cell = el('label', 'lb-sbb__cell');
        var input = el('input', 'lb-sbb__input');
        input.type = 'radio';
        input.name = id + '-o' + i;
        input.value = opt.value;
        input.setAttribute('aria-label', s.sub ? s.main + ', ' + s.sub : s.main);
        input.addEventListener('change', function () {
          if (!input.checked) return;
          var unchosen = chosenMask(st)[i] === false;
          if (o.sel.value === opt.value && !unchosen) return;
          o.sel.value = opt.value;
          /* fired even when the value already matches, if Fitted hasn't seen a
             choice yet: tapping the pre-filled first value counts as choosing it */
          o.sel.dispatchEvent(new Event('change', { bubbles: true }));
          queuePaint();
        });
        cell.appendChild(input);
        cell.appendChild(el('span', 'lb-sbb__main', s.main));
        if (s.sub) cell.appendChild(el('span', 'lb-sbb__sub', s.sub));
        chips.appendChild(cell);
        return { cell: cell, input: input, opt: opt };
      });
      g.appendChild(gh); g.appendChild(chips);
      pbody.appendChild(g);
      st.opts.push({ sel: o.sel, name: o.name, grp: g, gv: gv, bar: chips, cells: cells });
    });

    /* Place everything */
    var th = null;
    if (mobile) {
      if (colours.thumb) {
        th = el('img', 'lb-sbb__thumb');
        th.src = colours.thumb; th.alt = ''; th.width = 40; th.height = 40;
        slot.insertBefore(th, slot.firstChild);
      }
      /* Collection over price. Tapping it opens the drawer too (a pointer
         convenience; the button is the keyboard and screen-reader path). */
      var info = el('div', 'lb-sbb__id');
      var ik = el('span', 'lb-sbb__idk');
      var iv = el('span', 'lb-sbb__idv');
      info.appendChild(ik); info.appendChild(iv);
      var openFromInfo = function () { if (!st.open) { st.from = atc; setOpen(st, true); } };
      info.addEventListener('click', openFromInfo);
      if (th) { th.style.cursor = 'pointer'; th.addEventListener('click', openFromInfo); }
      slot.appendChild(info);
      st.info = { root: info, k: ik, v: iv };
      /* the drawer opens at the very top of the bar, above the free-gift
         strip, so the strip and the button row never move */
      bar.insertBefore(panel, bar.firstChild);
    } else {
      bar.appendChild(panel);                                    /* drawer opens downward under the row */
      st.title = bar.querySelector('.product-title');
      st.wrap = bar.closest('.pdp-sticky-atc-desktop__wrapper') || bar.parentElement;
      /* the theme pins its header for a few seconds after Add to cart (and on
         scroll up); watch it so the bar can step below it */
      var hdr = document.querySelector('.js-section-header');
      if (hdr && 'MutationObserver' in window) new MutationObserver(queuePaint).observe(hdr, { attributes: true, attributeFilter: ['class', 'style'] });
      /* Once something is chosen: the choices under the title, as a quiet
         text button that reopens the drawer (also after everything is
         chosen, when the bar's button adds to cart). */
      if (st.title) {
        var sum = el('button', 'lb-sbb__sum');
        sum.type = 'button';
        sum.hidden = true;
        sum.setAttribute('aria-controls', id + '-panel');
        sum.addEventListener('click', function () { st.from = sum; if (st.open) setOpen(st, false, true); else setOpen(st, true); });
        st.title.parentNode.insertBefore(sum, st.title.nextSibling);
        st.sum = sum;
      }
    }
    var scrim = el('div', 'lb-sbb__scrim');
    scrim.hidden = true;
    scrim.setAttribute('aria-hidden', 'true');
    bar.parentNode.insertBefore(scrim, bar);
    st.panel = panel; st.scrim = scrim;

    atc.setAttribute('aria-controls', panel.id);
    /* Repaint when Fitted relabels the button or the theme updates the page's
       price for a new variant. */
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(queuePaint);
      mo.observe(atc, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
      /* the theme replaces the price element itself on a variant change, so
         watch the product section (stable), not the price */
      var sec = document.querySelector('.main-pdp-section-redesign');
      if (sec && !sec.hasAttribute('data-lb-sbb-watched')) {
        sec.setAttribute('data-lb-sbb-watched', '');
        new MutationObserver(queuePaint).observe(sec, { childList: true, characterData: true, subtree: true });
      }
    }
    close.addEventListener('click', function () { setOpen(st, false, true); });
    /* Escape anywhere in the bar (the drawer, its button, the summary). */
    bar.addEventListener('keydown', function (e) { if (e.key === 'Escape' && st.open) { e.preventDefault(); setOpen(st, false, true); } });
    /* Focus leaving the bar (Tab past the drawer) closes it. */
    bar.addEventListener('focusout', function (e) {
      if (st.open && e.relatedTarget && !bar.contains(e.relatedTarget)) setOpen(st, false);
    });
    /* After a pick by pointer: guide to the next open option (keyboard picks
       move with the arrow keys, so focus is never pulled away from them). */
    panel.addEventListener('pointerdown', function () { st.pointer = true; }, true);
    panel.addEventListener('keydown', function () { st.pointer = false; }, true);

    bar.classList.add('lb-sbb');
    states.push(st);
    paint(st);
  }

  /* ---------- the button as the trigger ----------
     Window capture runs before Fitted's document listener and the theme's own
     click handlers on the button. While an option is still to choose, the
     click opens the drawer (or, already open, moves to the next open option)
     and goes no further. Once everything is chosen it is left alone. */
  window.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest(BUTTON);
    if (!btn) return;
    for (var k = 0; k < states.length; k++) {
      var st = states[k];
      if (st.atc !== btn) continue;
      if (firstOpen(st) === null) {                              /* complete: add to cart as today */
        if (st.open) setOpen(st, false);                         /* and the drawer folds away behind it */
        return;
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      st.from = btn;
      if (st.open) focusOption(st, null);
      else setOpen(st, true);
      return;
    }
  }, true);

  /* ---------- open / close ---------- */
  function setOpen(st, open, refocus) {
    if (open === st.open) return;
    states.forEach(function (o) { if (o !== st && o.open) setOpen(o, false); });
    st.open = open;
    if (st.atc.hasAttribute('aria-expanded')) st.atc.setAttribute('aria-expanded', open ? 'true' : 'false');
    clearTimeout(st.closing);
    var calm = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (open) {
      st.bar.classList.remove('lb-sbb--closing');
      st.scrim.classList.remove('is-closing');
      st.panel.hidden = false;
      st.scrim.hidden = false;
      st.bar.classList.add('lb-sbb--open');
    } else if (calm || !st.panel.offsetHeight) {
      st.bar.classList.remove('lb-sbb--open', 'lb-sbb--closing');
      st.panel.hidden = true;
      st.scrim.hidden = true;
    } else {
      /* fold away (200ms) with the backdrop fading, then hide */
      st.bar.classList.add('lb-sbb--closing');
      st.scrim.classList.add('is-closing');
      st.closing = setTimeout(function () {
        st.bar.classList.remove('lb-sbb--open', 'lb-sbb--closing');
        st.scrim.classList.remove('is-closing');
        st.panel.hidden = true;
        st.scrim.hidden = true;
      }, 200);
    }
    if (st.sum) st.sum.setAttribute('aria-expanded', open ? 'true' : 'false');
    queuePaint();                                                /* the chat bubble follows (liftChat) */
    if (open) {
      fitPanel(st);
      st.opts.forEach(function (o) { balance(o.bar); });
      focusOption(st, null);
    } else if (refocus) {
      /* Escape or ✕: focus goes back to what opened the drawer (the button) */
      var back = st.from && st.bar.contains(st.from) && st.from.offsetWidth ? st.from : st.atc;
      back.focus({ preventScroll: true });
    }
  }

  /* Opening lands on the decision still open: the first option still to
     choose, else the current value. That group's legend lights in brass for
     1.6s. Scrolls only inside the drawer, never the page. */
  function focusOption(st, i) {
    if (i == null) i = firstOpen(st);
    var o = i != null ? st.opts[i] : null;
    var target = (o && (o.bar.querySelector('.lb-sbb__input:checked') || o.bar.querySelector('.lb-sbb__input')))
      || st.panel.querySelector('.lb-sbb__input:checked') || st.panel.querySelector('.lb-sbb__input, .lb-sbb__swatch');
    if (target) target.focus({ preventScroll: true });
    if (o) mark(st, i);
    else st.opts.forEach(function (x) { x.grp.classList.remove('is-target'); });
  }

  /* Guidance, driven by what Fitted has registered (it can take a moment on
     the live store), not by a timer: called from every repaint with the first
     option still open. When that changes after a pick by pointer in the open
     drawer, the next open option lights up and scrolls into view; when nothing
     is left, the button that adds is ringed in brass for a moment (the bar's
     button on phones, the footer's on desktop). */
  function guide(st, next) {
    var prev = st.lastOpen;
    st.lastOpen = next;
    if (!st.open || !st.pointer || prev === undefined || next === prev) return;
    if (next !== null) { mark(st, next); return; }
    var b = st.foot ? st.foot.btn : st.atc;
    b.classList.remove('lb-sbb--ready');
    void b.offsetWidth;
    b.classList.add('lb-sbb--ready');
    clearTimeout(st.readyT);
    st.readyT = setTimeout(function () { b.classList.remove('lb-sbb--ready'); }, 1400);
  }
  /* Light one option's name in brass and bring it into view in the drawer. */
  function mark(st, i) {
    var o = st.opts[i];
    if (!o) return;
    var grp = o.grp, body = grp.parentNode;
    st.opts.forEach(function (x) { x.grp.classList.toggle('is-target', x === o); });
    clearTimeout(st.targetT);
    st.targetT = setTimeout(function () { grp.classList.remove('is-target'); }, 1600);
    var reveal = function () {
      var b = body.getBoundingClientRect(), g = grp.getBoundingClientRect();
      if (g.bottom > b.bottom || g.top < b.top) body.scrollTop += g.top - b.top - 12;
    };
    requestAnimationFrame(reveal);
    setTimeout(reveal, 340);
  }

  /* Phones: the drawer takes the height actually free above the bar (a phone
     held sideways has far less than 100svh - 240px), never less than 200px. */
  function fitPanel(st) {
    if (!st.mobile || st.panel.hidden) return;
    var row = st.bar.getBoundingClientRect().height - st.panel.getBoundingClientRect().height;
    st.panel.style.maxHeight = Math.max(200, Math.round(window.innerHeight - row - 16)) + 'px';
  }

  /* Chip rows: fewest rows that fit, then balanced (7 -> 4 + 3). */
  function balance(chips) {
    var n = chips.children.length, inner = chips.clientWidth, gap = 4;
    if (!n || inner <= 0) return;
    var ctx = balance.ctx || (balance.ctx = document.createElement('canvas').getContext('2d'));
    var w = 0;
    Array.prototype.forEach.call(chips.children, function (c) {
      ctx.font = '500 16px Lora, Georgia, serif';
      w = Math.max(w, ctx.measureText(c.querySelector('.lb-sbb__main').textContent).width);
      var s = c.querySelector('.lb-sbb__sub');
      if (s) { ctx.font = '400 10px Roboto, Arial, sans-serif'; w = Math.max(w, ctx.measureText(s.textContent.toUpperCase()).width + s.textContent.length * .2); }
    });
    var min = Math.max(48, Math.ceil(w + 16));
    var fit = Math.max(1, Math.floor((inner + gap) / (min + gap)));
    var rows = Math.ceil(n / fit);
    chips.style.setProperty('--lb-sbb-cols', String(Math.ceil(n / rows)));
  }

  /* ---------- repaint ---------- */
  function paint(st) {
    var mask = chosenMask(st);
    st.opts.forEach(function (o, i) {
      var s = splitUnits(o.sel.value);
      var chosen = mask[i];
      o.gv.textContent = chosen ? (s.sub ? s.main + ' · ' + s.sub : s.main) : 'Choose';
      o.gv.classList.toggle('is-prompt', !chosen);
      o.cells.forEach(function (c) {
        var on = chosen && c.input.value === o.sel.value;
        c.input.checked = on;
        c.cell.classList.toggle('is-on', on);
        c.cell.classList.toggle('is-off', c.opt.textContent.trim() !== c.opt.value);   /* the theme appends "- Unavailable" */
      });
    });
    /* The button announces the drawer only while it opens it. */
    if (mask.indexOf(false) > -1) st.atc.setAttribute('aria-expanded', st.open ? 'true' : 'false');
    else st.atc.removeAttribute('aria-expanded');
    guide(st, mask.indexOf(false) < 0 ? null : mask.indexOf(false));
    var price = currentPrice();
    renderPrice(price);
    var sm = summary(st, mask);
    if (st.info) fitInfo(st, price, sm);
    else { fitSum(st, sm); fitTitle(st); belowHeader(st); }
    if (st.foot) {
      st.foot.price.textContent = price ? price.amt : '';
      if (price && price.was) { st.foot.price.appendChild(document.createTextNode(' ')); st.foot.price.appendChild(el('s', 'lb-sbb__was', price.was)); }
      st.foot.btn.textContent = st.atc.textContent.trim();
      st.foot.btn.disabled = !!st.atc.disabled;
    }
    if (st.open) fitPanel(st);
  }

  /* Phone label over price, never cut. The label is the choices once
     anything is chosen (named, then values only), else the collection; the
     sale's struck price goes before the label does; last, the photo steps
     aside (long rupiah prices on 320px phones). */
  function fitInfo(st, price, sm) {
    var f = st.info;
    var amt = price ? price.amt : '', was = price ? price.was : '';
    /* the colour leads the label, so a one-option product still says which
       colour you're buying (and the photo/label open the drawer to change it) */
    var c = st.colours.links.length > 1 ? st.colours.name : '';
    var pre = function (l) { return c && l ? c + ' · ' + l : (l || c); };
    var labels = sm.any ? [pre(sm.full), pre(sm.short), sm.full, sm.short] : [pre(collection()), collection()];
    var tiers = [];
    labels.forEach(function (l) { tiers.push([l, was, false]); });
    labels.forEach(function (l) { if (was) tiers.push([l, '', false]); });
    tiers.push(['', was, false], ['', '', false], ['', '', true]);
    for (var t = 0; t < tiers.length; t++) {
      f.k.textContent = tiers[t][0];
      f.v.textContent = amt;
      if (tiers[t][1]) { f.v.appendChild(document.createTextNode(' ')); f.v.appendChild(el('s', 'lb-sbb__was', tiers[t][1])); }
      st.bar.classList.toggle('lb-sbb--tight', tiers[t][2]);
      if (!f.root.offsetWidth || (fits(f.k) && fits(f.v))) break;
    }
  }
  /* Desktop: the choices under the title, named, else values only. */
  function fitSum(st, sm) {
    var s = st.sum;
    if (!s) return;
    s.hidden = !sm.any;
    if (!sm.any) return;
    /* the text sits in its own span: the button's tap skirt would otherwise
       count as overflow */
    var t = s.firstChild || s.appendChild(el('span', 'lb-sbb__sumt'));
    t.textContent = sm.full;
    s.setAttribute('aria-label', 'Your selection: ' + sm.full + '. Change');
    if (t.offsetWidth && !fits(t)) t.textContent = sm.short;
  }
  function fits(n) { return !n.offsetWidth || (n.scrollWidth <= n.clientWidth + 1 && n.scrollHeight <= n.clientHeight + 1); }

  /* Desktop: while the theme's header is pinned at the top of the screen
     (for a few seconds after Add to cart, with the cart notice inside it, and
     when scrolling up), the bar sits just below it instead of under it; when
     the header hides, the bar slides back to the top. */
  function belowHeader(st) {
    var w = st.wrap;
    if (!w) return;
    var hdr = document.querySelector('.js-section-header');
    var off = 0;
    if (hdr) {
      var pos = getComputedStyle(hdr).position, r = hdr.getBoundingClientRect();
      if ((pos === 'sticky' || pos === 'fixed') && r.top <= 1 && r.bottom > 0) off = Math.round(r.bottom);
    }
    var v = off ? off + 'px' : '';
    /* the theme's tw-top-0 is !important, so the step down has to be too */
    if (w.style.top !== v) { if (v) w.style.setProperty('top', v, 'important'); else w.style.removeProperty('top'); }
    st.bar.style.setProperty('--lb-sbb-top', off + 'px');
  }

  /* Desktop title: two lines, never cut; long names step the size down. With
     the choices showing under it, title and choices together stay within the
     photo's height, so the bar never grows. */
  function fitTitle(st) {
    var t = st.title;
    if (!t || !t.offsetWidth) return;
    var steps = [[18, 24], [16, 21], [15, 20], [14, 18]];
    var lines = function () { return Math.round(t.scrollHeight / parseFloat(getComputedStyle(t).lineHeight)); };
    var img = st.bar.querySelector('.tw-w-full > .tw-flex:first-child > div:first-child > img');
    var room = st.sum && !st.sum.hidden && img && img.offsetHeight ? img.offsetHeight - st.sum.offsetHeight - 4 : Infinity;
    var fit = function () {
      t.style.fontSize = ''; t.style.lineHeight = '';
      for (var i = 0; i < steps.length && (lines() > 2 || t.offsetHeight > room); i++) {
        t.style.fontSize = steps[i][0] + 'px';
        t.style.lineHeight = steps[i][1] + 'px';
      }
      return lines() <= 2 && t.offsetHeight <= room;
    };
    /* first with the colour swatches in the bar; if the title still can't
       fit, the swatches step aside (the drawer shows the colours instead) */
    st.bar.classList.remove('lb-sbb--noswatch');
    if (!fit() && st.colours.links.length > 1) { st.bar.classList.add('lb-sbb--noswatch'); fit(); }
    /* a title that still needs the room (three lines at the smallest size):
       the choices step aside rather than the bar growing; the drawer lists them */
    if (room !== Infinity && t.offsetHeight > room) st.sum.hidden = true;
  }

  /* Phones: Shopify Inbox's chat bubble sits bottom-left, over the bar's
     photo. While the bar shows, the bubble is lifted to just above it through
     the app's own public setting (--shopify-chat-activator-offset, which only
     moves the bubble, not the open chat); while the drawer is open it fades
     out; when the bar hides, the app's default comes back. */
  var lifted = '';
  function liftChat() {
    var st = states.filter(function (s) { return s.mobile; })[0];
    var root = document.documentElement;
    var h = st && st.bar.offsetHeight ? st.bar.getBoundingClientRect().height - (st.panel.hidden ? 0 : st.panel.getBoundingClientRect().height) : 0;
    var v = h > 0 ? Math.round(h + 16) + 'px' : '';
    if (v !== lifted) {
      lifted = v;
      if (v) root.style.setProperty('--shopify-chat-activator-offset', v);
      else root.style.removeProperty('--shopify-chat-activator-offset');
    }
    root.classList.toggle('lb-sbb-drawer-open', !!(st && st.open));
  }

  var queued = false;
  function queuePaint() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      states.forEach(function (st) { paint(st); if (st.open) st.opts.forEach(function (o) { balance(o.bar); }); });
      liftChat();
    });
  }

  /* Repaint after any change or click on the page (Fitted writes the bar's
     selects silently), on resize, and whenever a web font finishes loading. */
  window.addEventListener('change', queuePaint, true);
  window.addEventListener('click', queuePaint, true);
  window.addEventListener('resize', queuePaint);   /* also refits the phone drawer to the space left */
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(queuePaint);
  if (document.fonts && document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', queuePaint);

  /* A tap outside the bar closes the drawer. */
  document.addEventListener('pointerdown', function (e) {
    states.forEach(function (st) {
      if (st.open && !st.bar.contains(e.target)) setOpen(st, false);
    });
  }, true);

  /* The bar hides when scrolled back up (pdpIntersect): close with it. The
     close waits a frame, so the observer never loops. */
  var ro = 'ResizeObserver' in window ? new ResizeObserver(function (entries) {
    entries.forEach(function (e) {
      var st = states.filter(function (s) { return s.bar === e.target; })[0];
      if (!st) return;
      if (!e.contentRect.width && st.open) requestAnimationFrame(function () { setOpen(st, false); });
      else queuePaint();
    });
  }) : null;

  function init() {
    document.querySelectorAll(BARS).forEach(function (bar) {
      build(bar);
      if (ro && bar.classList.contains('lb-sbb')) ro.observe(bar);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.LBStickyATCB = { version: '1.1.3', init: init, states: states };
})();
